import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Save, Tag } from 'lucide-react';
import {
  fetchChargerTariff, fetchLocationTariff, getDisplayErrorMessage, tariffConflictView,
  updateChargerTariff, updateLocationTariff,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { isSiteCallerUser } from '@/lib/siteDashboard';
import {
  canEditChargerTariff, canEditLocationTariff, chargerConnectionFeeText, choiceBody, effectiveHeading,
  locationConnectionFeeText, noneChoiceLabel, staleChoiceLabel, storedChoice, tariffDetailRows, tariffOptionLabel,
  type ChargerTariffView, type LocationTariffView, type TariffSummary,
} from '@/lib/tariffAssignment';
import formStyles from '@/components/ui/Form.module.css';
import cardStyles from '@/components/ui/ResidentChargersCard.module.css';
import styles from './TariffAssignmentCard.module.css';

type View = LocationTariffView | ChargerTariffView;

/**
 * Pricing on the location and charger pages (Warren, 2026-10-25: "we must be
 * able to set our own prices in Admin").
 *
 *   location   the default tariff: what every charger here without a tariff
 *              of its own bills
 *   charger    "Use the location default (<name>)" or a tariff of its own,
 *              which wins over the location default
 *
 * Both show the tariff that bills now and where it comes from, with a
 * warning when it is missing, inactive or the fallback rate. The choices are
 * the operator account's ACTIVE tariffs in the location's currency (the Api
 * refuses anything else and says why). Edit needs the permission string the
 * PATCH checks and is never offered to a Site; everyone else reads. Save
 * sends what was loaded, so a record someone changed meanwhile is refused
 * (409) and reloaded rather than overwritten. Every change is one audit row
 * on the Api. A session already running keeps the price it started with.
 */
export function TariffAssignmentCard({ kind, id, onNavigate, onSaved }: {
  kind: 'location' | 'charger';
  id: string;
  onNavigate?: (path: string) => void;
  onSaved?: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const isSite = isSiteCallerUser(user);
  const [view, setView] = useState<View | null>(null);
  const [choice, setChoice] = useState('');
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // A caller the read refuses (403) sees no card rather than an error.
  const [hidden, setHidden] = useState(false);

  const adopt = (v: View) => { setView(v); setChoice(storedChoice(v)); };
  const reload = async () => {
    setLoadError('');
    try {
      adopt(kind === 'location' ? await fetchLocationTariff(id) : await fetchChargerTariff(id));
    } catch (err) {
      if ((err as { response?: Response }).response?.status === 403) { setHidden(true); return; }
      setLoadError(await getDisplayErrorMessage(err, 'Could not load the pricing for this ' + kind + '.'));
    }
  };
  useEffect(() => { setView(null); void reload(); }, [kind, id]);

  if (hidden) return null;
  if (loadError) {
    return (
      <section className={cardStyles.card} aria-labelledby={`tariff-card-${id}`}>
        <h2 className={cardStyles.cardTitle} id={`tariff-card-${id}`}><Tag size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Pricing</h2>
        <div className={cardStyles.alertErr}>{loadError}</div>
      </section>
    );
  }
  if (!view) {
    return (
      <section className={cardStyles.card} aria-busy="true">
        <h2 className={cardStyles.cardTitle}><Tag size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Pricing</h2>
        <p className={styles.note}><Loader2 size={12} className={styles.spin} /> Loading the tariff...</p>
      </section>
    );
  }

  const canEdit = view.kind === 'location'
    ? canEditLocationTariff(user, isSite, view.can_edit)
    : canEditChargerTariff(user, isSite, view.can_edit);
  const stored = storedChoice(view);
  const dirty = choice !== stored;
  const storedRef = view.kind === 'location' ? view.default_tariff : view.own_tariff;
  const offered = new Map(view.candidates.map((t) => [t._id, t]));
  // The stored tariff stays in the list even when it is no longer offered,
  // flagged, so the page never shows a setting the record does not have.
  const staleLabel = stored && !offered.has(stored)
    ? staleChoiceLabel(storedRef) ?? (storedRef.tariff ? `${tariffOptionLabel(storedRef.tariff)} (not in this location's currency)` : null)
    : null;
  const chosen: TariffSummary | null = choice ? offered.get(choice) ?? null : null;
  const effective = view.effective.tariff;
  const connectionFee = view.kind === 'charger' ? chargerConnectionFeeText(view) : locationConnectionFeeText(view);
  const title = view.kind === 'location' ? 'Default tariff' : 'Tariff';
  const currency = view.location.currency;

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError('');
    setSavedAt(null);
    try {
      const body = choiceBody(view, choice);
      const next = view.kind === 'location'
        ? await updateLocationTariff(id, body as { default_tariff_id: string | null; expected_default_tariff_id: string | null })
        : await updateChargerTariff(id, body as { tariff_id: string | null; expected_tariff_id: string | null });
      adopt(next);
      setSavedAt(Date.now());
      onSaved?.();
    } catch (err) {
      const current = await tariffConflictView<View>(err);
      if (current) adopt(current);
      setError(await getDisplayErrorMessage(err, 'Could not save the tariff.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={cardStyles.card} aria-labelledby={`tariff-card-${id}`}>
      <div className={cardStyles.cardTitleRow}>
        <h2 className={cardStyles.cardTitle} id={`tariff-card-${id}`}>
          <Tag size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Pricing
        </h2>
        {currency && <span className={styles.note}>Location currency: {currency}</span>}
      </div>

      {view.warnings.length > 0 && (
        <div className={styles.warnList} role="status">
          {view.warnings.map((w) => (
            <div key={w} className={styles.warn}><AlertTriangle size={14} /><span>{w}</span></div>
          ))}
        </div>
      )}

      <div className={styles.effective}>
        <p className={styles.effectiveHeading}>{effectiveHeading(view)}</p>
        {effective && (
          <dl className={styles.details}>
            {tariffDetailRows(effective, connectionFee).map((r) => (
              <div key={r.label} className={styles.detail}>
                <dt className={styles.detailLabel}>{r.label}</dt>
                <dd className={styles.detailValue}>{r.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {view.kind === 'location' && view.chargers.total > 0 && (
          <p className={styles.note}>
            {view.chargers.total} charger{view.chargers.total === 1 ? '' : 's'} here:{' '}
            {view.chargers.using_default} without a tariff of {view.chargers.using_default === 1 ? 'its' : 'their'} own
            {view.chargers.own_tariff > 0 ? `, ${view.chargers.own_tariff} with a tariff of ${view.chargers.own_tariff === 1 ? 'its' : 'their'} own` : ''}
            {view.chargers.own_tariff_skipped > 0 ? ` (${view.chargers.own_tariff_skipped} of them with an inactive or missing one, which billing skips)` : ''}.
          </p>
        )}
      </div>

      {canEdit ? (
        <div className={styles.control}>
          {error && <div className={cardStyles.alertErr}>{error}</div>}
          {savedAt && !error && <div className={`${cardStyles.alertOk} ${styles.fadeIn}`}>Saved. New sessions use this price.</div>}
          <label className={formStyles.label} htmlFor={`tariff-choice-${id}`}>{title}</label>
          <div className={styles.controlRow}>
            <select
              id={`tariff-choice-${id}`}
              className={formStyles.select}
              value={choice}
              disabled={saving}
              onChange={(e) => { setChoice(e.target.value); setSavedAt(null); setError(''); }}
            >
              <option value="">{noneChoiceLabel(view)}</option>
              {staleLabel && <option value={stored}>{staleLabel}</option>}
              {view.candidates.map((t) => (
                <option key={t._id} value={t._id}>{tariffOptionLabel(t)}</option>
              ))}
            </select>
            <button type="button" className={styles.saveBtn} disabled={!dirty || saving} onClick={() => { void save(); }}>
              {saving ? <><Loader2 size={13} className={styles.spin} /> Saving...</> : <><Save size={13} /> Save</>}
            </button>
          </div>
          {dirty && (
            <p className={`${styles.preview} ${styles.fadeIn}`}>
              {chosen
                ? <>After saving: <strong>{tariffOptionLabel(chosen)}</strong>{chosen.idle_rate_cents ? '' : ', no idle fee'}.</>
                : choice === ''
                  ? <>After saving: {view.kind === 'charger' ? 'the location default applies' : 'chargers here without their own tariff use the account default, or the fallback rate'}.</>
                  : null}
            </p>
          )}
          <p className={styles.note}>
            Only active {currency ?? ''} tariffs are offered.
            {view.other_currency_count > 0 ? ` ${view.other_currency_count} tariff${view.other_currency_count === 1 ? ' is' : 's are'} in another currency.` : ''}
            {' '}A change prices sessions that start after you save. A session in progress keeps the price it started with.
            {view.candidates.length === 0 && currency ? ` No active ${currency} tariff yet: create one on the Tariffs page.` : ''}
          </p>
          {onNavigate && (
            <div>
              <button type="button" className={styles.linkBtn} onClick={() => onNavigate('/tariffs')}>Manage tariffs</button>
            </div>
          )}
        </div>
      ) : (
        <p className={styles.note}>
          {isSite ? 'Prices are set by EV Initiative.' : 'You can view this pricing. Changing it needs permission to edit this ' + (view.kind === 'location' ? 'location' : 'charger') + '.'}
        </p>
      )}
    </section>
  );
}
